import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3333/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to get cookie by name
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

// Axios interceptor to add CSRF token to requests
api.interceptors.request.use((config) => {
  const token = getCookie('XSRF-TOKEN');
  if (token) {
    config.headers['X-XSRF-TOKEN'] = decodeURIComponent(token);
  }
  return config;
});

export async function getShippingRates(fromCountry, toCountry, weight, postalCode, city) {
  const params = {
    from_country: fromCountry,
    to_country: toCountry,
    weight: weight
  };

  if (postalCode) params.to_postal_code = postalCode;
  if (city) params.to_city = city;

  try {
    const response = await api.get('/shipping-rates', { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch shipping rates');
  }
}

export async function createOrder(orderData) {
  try {
    const response = await api.post('/order', orderData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create order');
  }
}

// Stripe Checkout Session
export async function createCheckoutSession(payload) {
  try {
    const response = await api.post('/create-checkout-session', payload);
    return response.data;
  } catch (error) {
    console.error('API Error:', error.response?.data);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to create checkout session';
    throw new Error(errorMessage);
  }
}

// Get order status by Stripe session ID
export async function getOrderBySession(sessionId) {
  try {
    const response = await api.get(`/order-status/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error('API Error:', error.response?.data);
    throw new Error(error.response?.data?.error || 'Failed to fetch order status');
  }
}

// LemonSqueezy Payment
export async function createLemonSqueezyCheckout(payload) {
  try {
    // payload: { productId, variantId, email, name, customData }
    const response = await api.post('/lemonsqueezy/checkout', payload);
    return response.data;
  } catch (error) {
    console.error('LemonSqueezy API Error:', error.response?.data);
    const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Failed to create checkout';
    throw new Error(errorMessage);
  }
}

export async function getLemonSqueezyProducts() {
  try {
    const response = await api.get('/lemonsqueezy/products');
    return response.data.products;
  } catch (error) {
    console.error('Error fetching LemonSqueezy products:', error);
    return [];
  }
}

export async function getLemonSqueezyVariants(productId) {
  try {
    const response = await api.get(`/lemonsqueezy/products/${productId}/variants`);
    return response.data.variants;
  } catch (error) {
    console.error('Error fetching LemonSqueezy variants:', error);
    return [];
  }
}

// Reviews
export const getReviews = async (productId) => {
  try {
    const response = await api.get(`/products/${productId}/reviews`);
    return response.data;
  } catch (error) {
    console.error('Error fetching reviews:', error);
    return { reviews: [] };
  }
};

export const createReview = async (productId, reviewData) => {
  try {
    const response = await api.post(`/products/${productId}/reviews`, reviewData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to create review');
  }
};

// Products
export const getProducts = async () => {
  try {
    const response = await api.get('/products');
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    return [];
  }
};

/**
 * Un seul produit, pour la fiche /product/:id.
 *
 * Contrairement à getProducts, on ne masque pas l'erreur : la fiche doit
 * pouvoir distinguer « ce produit n'existe pas » (404) d'une panne réseau,
 * sinon les deux cas affichent le même écran vide.
 */
export const getProduct = async (id) => {
  const response = await api.get(`/products/${encodeURIComponent(id)}`);
  return response.data;
};

export const createProduct = async (data) => {
  try {
    const response = await api.post('/admin/products', data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to create product');
  }
};

export const updateProduct = async (id, data) => {
  try {
    const response = await api.patch(`/admin/products/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to update product');
  }
};

export const deleteProduct = async (id) => {
  try {
    await api.delete(`/admin/products/${id}`);
    return true;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to delete product');
  }
};

/**
 * Adresse complète d'un document servi par l'API (facture, note de crédit) à
 * partir du chemin signé que renvoie le serveur (« /facture/JR-2026-0001?cle=… »).
 */
export const urlDocument = (chemin) => `${api.defaults.baseURL}${chemin}`;

/**
 * « Retrouver ma facture » (acheteur sans compte) : numéro de commande ou de
 * facture + e-mail d'achat. En POST : l'adresse ne voyage jamais dans une URL.
 */
export const retrouverMaFacture = async (numero, email) => {
  const response = await api.post('/ma-facture', { numero, email });
  return response.data;
};

/** Administration : toutes les factures émises par le site, avec recherche (50 par page). */
export const listerFacturesDuSite = async (q, page = 1) => {
  const response = await api.get('/admin/factures', { params: { q: q || undefined, page } });
  return response.data;
};

/** Administration : émet la note de crédit totale de la facture d'une commande. */
export const emettreNoteDeCredit = async (orderId, motif) => {
  const response = await api.post(`/admin/orders/${orderId}/note-de-credit`, { motif });
  return response.data;
};

/**
 * PDF de l'étiquette d'expédition d'une commande (admin).
 *
 * On passe par l'instance axios authentifiée plutôt que par un window.open :
 * l'URL Sendcloud exige les clés API, c'est donc le serveur qui relaie le
 * fichier, et un simple lien ne porterait pas la session. Le blob permet en
 * outre de lire proprement le message quand il n'y a pas d'étiquette.
 */
export const getOrderLabel = async (orderId) => {
  const response = await api.get(`/admin/orders/${orderId}/label`, { responseType: 'blob' });
  return response.data;
};

// Orders
export const getOrders = async () => {
  try {
    const response = await api.get('/orders');
    return response.data;
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch orders');
  }
};

export default api;
